import React from 'react';
import { motion } from 'motion/react';
import { CalendarDays, Clock, MapPin } from 'lucide-react';
import type { WeddingEventItem } from '@/src/types';
import { Reveal, formatDisplayDate } from './shared';

type Props = {
  events: WeddingEventItem[];
};

function timeRange(event: WeddingEventItem) {
  if (event.startTime && event.endTime) return `${event.startTime} - ${event.endTime}`;
  return event.startTime || '';
}

function mapUrlFor(event: WeddingEventItem) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue)}`;
}

export function WeddingTimelineSection({ events }: Props) {
  const visibleEvents = events.filter((event) => event.title?.trim() && event.date?.trim() && event.venue?.trim());
  if (visibleEvents.length === 0) return null;

  return (
    <section className="relative overflow-hidden bg-[#f8f1e5] px-5 py-20 text-[#1f1b15] sm:px-8 lg:px-14 lg:py-28">
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#17140e]/10 to-transparent" />
      <Reveal className="mb-14 max-w-5xl">
        <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#a15f54]">Wedding Events</p>
        <h2 className="mt-4 text-[clamp(2.35rem,5.8vw,6rem)] font-semibold leading-[0.98] tracking-normal">
          Every celebration has its own rhythm.
        </h2>
      </Reveal>

      <div className="relative">
        <div className="absolute left-4 top-0 hidden h-full w-px bg-[#1f1b15]/18 md:block" />
        <div className="space-y-16">
          {visibleEvents.map((event, index) => (
            <Reveal key={`${event.title}-${index}`} delay={index * 0.05}>
              <motion.article
                whileHover={{ x: 6 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="grid min-h-[58vh] gap-6 border-t border-[#1f1b15]/18 pt-7 md:grid-cols-[0.28fr_0.72fr] md:border-t-0 md:pl-12"
              >
                <div className="relative">
                  <span className="absolute -left-[3.1rem] top-1 hidden h-8 w-8 rounded-full border border-[#a15f54] bg-[#f8f1e5] md:block" />
                  <p className="text-5xl font-semibold text-[#a15f54]/34">0{index + 1}</p>
                  <p className="mt-4 flex min-w-0 items-start gap-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#75685b]">
                    <CalendarDays size={17} className="mt-0.5 shrink-0 text-[#a15f54]" />
                    {formatDisplayDate(event.date)}
                  </p>
                </div>
                <div className="grid min-w-0 gap-6 lg:grid-cols-[1fr_0.45fr]">
                  <div className="min-w-0">
                    <h3 className="text-3xl font-semibold leading-tight sm:text-4xl">{event.title}</h3>
                    {event.description && <p className="mt-5 max-w-2xl text-lg leading-8 text-[#51463a]">{event.description}</p>}
                    <div className="mt-7 grid min-w-0 gap-4 text-sm text-[#51463a] sm:grid-cols-2">
                      {timeRange(event) && (
                        <p className="flex min-w-0 items-start gap-3">
                          <Clock size={17} className="mt-0.5 shrink-0 text-[#a15f54]" />
                          <span className="min-w-0 break-words">{timeRange(event)}</span>
                        </p>
                      )}
                      <a
                        href={mapUrlFor(event)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex min-w-0 items-start gap-3 font-semibold text-[#8f514a] underline decoration-[#a15f54]/35 underline-offset-4 transition hover:text-[#6f3f39] hover:decoration-[#a15f54] focus:outline-none focus:ring-2 focus:ring-[#a15f54]/35"
                      >
                        <MapPin size={17} className="mt-0.5 shrink-0" />
                        <span className="min-w-0 break-words">{event.venue}</span>
                      </a>
                    </div>
                  </div>
                  {event.coverImageUrl && (
                    <div className="aspect-[4/3] overflow-hidden bg-[#211712] lg:aspect-[4/5]">
                      <img src={event.coverImageUrl} alt="" className="h-full w-full object-cover" />
                    </div>
                  )}
                </div>
              </motion.article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
